import { FFmpeg } from "@ffmpeg/ffmpeg";
import { logErrorToCrashlytics } from "../../firebase";

/**
 * Sets up audio recording by merging local and remote audio streams
 */
export const setupAudioRecording = (
  localStream,
  remoteStream,
  recordedChunks,
  mediaRecorder,
) => {
  try {
    const audioContext = new AudioContext();

    const localSource = audioContext.createMediaStreamSource(localStream);
    const remoteSource = audioContext.createMediaStreamSource(remoteStream);

    const destination = audioContext.createMediaStreamDestination();
    localSource.connect(destination);
    remoteSource.connect(destination);

    recordedChunks.current = [];

    mediaRecorder.current = new MediaRecorder(destination.stream, {
      mimeType: "audio/webm",
    });

    mediaRecorder.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.current.push(event.data);
      }
    };

    mediaRecorder.current.start();
    console.log("Recording started");
  } catch (error) {
    console.error("Error setting up audio recording:", error);
  }
};

/**
 * Converts recorded WebM audio chunks to MP3 using FFmpeg in browser
 */
const convertWebMToMP3 = async (webmBlob) => {
  const ffmpeg = new FFmpeg();
  await ffmpeg.load();

  const inputName = "input.webm";
  const outputName = "output.mp3";

  const buffer = new Uint8Array(await webmBlob.arrayBuffer());
  await ffmpeg.writeFile(inputName, buffer);

  await ffmpeg.exec(["-i", inputName, "-b:a", "192k", outputName]);

  const mp3Data = await ffmpeg.readFile(outputName);
  return new Blob([mp3Data.buffer], { type: "audio/mpeg" });
};

/**
 * Sends the MP3 recording to the API endpoint
 */
export const sendRecordingToApi = async (recordedChunks, callId) => {
  try {
    if (!recordedChunks?.current?.length) {
      console.error("No recorded chunks available");
      logErrorToCrashlytics(new Error("No recorded chunks available"), {
        context: "sendRecordingToApi",
        callId,
        hasChunks: false,
      });
      return { success: false, error: "No recorded chunks available" };
    }

    if (!callId) {
      console.error("Call ID is required");
      logErrorToCrashlytics(new Error("Call ID is required"), {
        context: "sendRecordingToApi",
        callId: null,
        hasChunks: !!recordedChunks?.current?.length,
      });
      return { success: false, error: "Call ID is required" };
    }

    const webmBlob = new Blob(recordedChunks.current, { type: "audio/webm" });

    console.log("Converting to MP3...");
    const mp3Blob = await convertWebMToMP3(webmBlob);

    const filename = `recording-${callId}.mp3`;
    const recordingFile = new File([mp3Blob], filename, { type: "audio/mpeg" });

    const formData = new FormData();
    formData.append("recording", recordingFile);

    console.log(`Sending recording for call ${callId} to API...`);

    const response = await fetch(
      `https://callcenter.teech.tv/api/calls/${callId}/save-recording`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!response.ok) {
      const error = new Error(
        `API error: ${response.status} ${response.statusText}`,
      );
      logErrorToCrashlytics(error, {
        context: "sendRecordingToApi API response",
        callId,
        status: response.status,
        statusText: response.statusText,
        chunksLength: recordedChunks?.current?.length || 0,
      });
      throw error;
    }

    const result = await response.json();
    console.log("Recording saved successfully:", result);

    return result;
  } catch (error) {
    console.error("Error sending recording to API:", error);
    logErrorToCrashlytics(error, {
      context: "sendRecordingToApi catch block",
      callId,
      chunksLength: recordedChunks?.current?.length || 0,
      errorName: error.name,
      errorMessage: error.message,
    });
    return { success: false, error: error.message };
  }
};
